import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Calculator } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const SCOPE_LABELS = {
  SCOPE_1: "Scope 1 — Emissioni dirette",
  SCOPE_2_LOCATION: "Scope 2 — Location-based",
  SCOPE_2_MARKET: "Scope 2 — Market-based",
  NONE: "Fuori inventario GHG",
};

const STATUS_STYLES = {
  CALCULATED: "bg-sky-50 text-sky-800 border-sky-300",
  MISSING_FACTOR: "bg-amber-50 text-amber-800 border-amber-300",
  TO_VERIFY: "bg-amber-50 text-amber-800 border-amber-300",
  VERIFIED: "bg-green-50 text-green-800 border-green-300",
  APPROVED: "bg-green-700 text-white border-green-700",
  OUTDATED: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function EmissionsTab({ companyId, periodId, refreshKey }) {
  const [calcs, setCalcs] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!companyId || !periodId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${periodId}/environmental/calculations`);
      setCalcs(data);
    } catch (err) {
      toast.error("Errore caricamento calcoli", { description: formatApiError(err) });
    }
  }, [companyId, periodId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="space-y-4" data-testid="emissions-tab">
      <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">
        Ogni risultato è calcolato dal motore deterministico: dato di attività × fattore di emissione.
        Espandi un calcolo per vedere formula, fattore e fonte utilizzati.
      </p>
      {calcs.length === 0 && (
        <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-600" data-testid="calculations-empty">
          Nessun calcolo disponibile. Inserisci prima un consumo nella sezione Energia.
        </CardContent></Card>
      )}
      <div className="space-y-2">
        {calcs.map((c) => (
          <Card key={c.id} className="border-slate-200" data-testid={`calc-${c.id.slice(0, 8)}`}>
            <CardContent className="p-4">
              <button type="button" className="w-full flex flex-wrap items-center justify-between gap-2 text-left"
                onClick={() => setOpenId(openId === c.id ? null : c.id)}
                data-testid={`calc-expand-${c.id.slice(0, 8)}`}
                aria-expanded={openId === c.id}>
                <div className="flex flex-wrap items-center gap-2">
                  <Calculator className="w-4 h-4 text-green-700" aria-hidden="true" />
                  <span className="text-sm font-semibold text-slate-900">
                    {c.activity_value} {c.activity_unit}
                  </span>
                  <Badge variant="outline" className="text-[11px] bg-slate-50">{SCOPE_LABELS[c.scope] || c.scope}</Badge>
                  <Badge variant="outline" className={`text-[11px] ${STATUS_STYLES[c.status] || ""}`}>
                    {c.status}{c.version > 1 ? ` · v${c.version}` : ""}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900" data-testid={`calc-result-${c.id.slice(0, 8)}`}>
                    {c.ghg_emissions_tco2e != null ? `${c.ghg_emissions_tco2e} tCO₂e` : "—"}
                  </span>
                  {openId === c.id ? <ChevronUp className="w-4 h-4 text-slate-400" aria-hidden="true" /> : <ChevronDown className="w-4 h-4 text-slate-400" aria-hidden="true" />}
                </div>
              </button>
              {openId === c.id && (
                <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm space-y-2" data-testid={`calc-detail-${c.id.slice(0, 8)}`}>
                  <p className="font-semibold text-slate-900">Come è stato calcolato</p>
                  {c.factor_value_snapshot != null ? (
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 text-slate-700">
                      <div><dt className="text-xs text-slate-500">Dato di attività</dt><dd className="font-medium">{c.activity_value} {c.activity_unit}</dd></div>
                      <div><dt className="text-xs text-slate-500">Fattore di emissione</dt><dd className="font-medium">{c.factor_value_snapshot} {c.factor_unit_snapshot}</dd></div>
                      <div><dt className="text-xs text-slate-500">Fonte del fattore</dt><dd className="font-medium">{c.factor_source_snapshot}</dd></div>
                      <div><dt className="text-xs text-slate-500">Metodologia</dt><dd className="font-medium">{c.calculation_method}{c.factor_gwp_snapshot ? ` · GWP ${c.factor_gwp_snapshot}` : ""}</dd></div>
                    </dl>
                  ) : (
                    <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" role="alert">
                      {c.notes || "FATTORE DI EMISSIONE DA CONFIGURARE — il risultato non viene prodotto senza un fattore verificato."}
                    </p>
                  )}
                  {c.ghg_emissions_kgco2e != null && (
                    <p className="font-mono text-xs text-slate-600 pt-1">
                      {c.activity_value} {c.activity_unit} × {c.factor_value_snapshot} {c.factor_unit_snapshot}
                      {" "}= {Math.round(c.ghg_emissions_kgco2e * 100) / 100} kgCO₂e = {c.ghg_emissions_tco2e} tCO₂e
                    </p>
                  )}
                  <p className="text-xs text-slate-500">Calcolato il {new Date(c.calculated_at).toLocaleString("it-IT")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
